import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDistrictGuard } from "@/hooks/useDistrictGuard";
import { invalidateQuery } from "@/hooks/useQueryCore";
import { notesStorage } from "@/lib/notes-storage";
import * as notesService from "@/lib/notes-service";
import { NavHistory } from "@/lib/nav-history";

const AUTOSAVE_DELAY = 600; // ms

/**
 * @param {string|null} noteId — null = new note
 */
export function useNoteEditor(noteId) {
  const { districtId } = useDistrictGuard();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null); // timestamp of last save for "Guardado" indicator

  const autosaveTimer = useRef(null);
  const latestNote = useRef(null); // stable ref for the save closure

  // Extract primitives so the load effect depends on stable values, not the
  // URLSearchParams object (which is non-primitive and recreated each render).
  // These are only consumed when noteId is null (new-note path).
  const linkedType   = searchParams.get("linked_type")  || "general";
  const linkedId     = searchParams.get("linked_id")    || null;
  const linkedLabel  = searchParams.get("linked_label") || null;
  const contentParam = searchParams.get("content")      || null;
  const titleParam   = searchParams.get("title")        || null;

  // ── Load ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!districtId) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        if (noteId) {
          const existing = await notesStorage.getById(noteId);
          // Treat soft-deleted notes (_deleted: true) as non-existent so
          // back-navigation to a just-deleted path shows nothing instead of
          // reopening the note.
          const resolved = existing?._deleted ? null : (existing ?? null);
          if (!cancelled) {
            setNote(resolved);
            if (resolved) notesStorage.touchViewed(noteId).catch(() => {});
          }
        } else {
          // New note — entity link was pre-extracted from URL params above
          const draft = await notesService.createNote(
            { linked_type: linkedType, linked_id: linkedId, linked_label: linkedLabel, ...(contentParam ? { content: contentParam } : {}), ...(titleParam ? { title: titleParam } : {}) },
            districtId
          );
          if (!cancelled) {
            nav(`/notes/${draft.id}`, { replace: true });
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [noteId, districtId, nav, linkedType, linkedId, linkedLabel, contentParam, titleParam]);

  // Sync latest note ref without causing re-renders in the save closure
  useEffect(() => {
    latestNote.current = note;
  }, [note]);

  // ── Flush on unmount ────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (autosaveTimer.current) {
        clearTimeout(autosaveTimer.current);
        // Flush immediately on unmount if there's a pending save
        if (latestNote.current?.dirty) {
          notesService.updateNote(
            latestNote.current.id,
            {
              title:           latestNote.current.title,
              content:         latestNote.current.content,
              tags:            latestNote.current.tags,
              note_type:       latestNote.current.note_type,
              checklist_items: latestNote.current.checklist_items,
            },
            districtId
          ).catch(() => {});
          invalidateQuery(["notes", districtId]);
          invalidateQuery(["search-corpus", districtId]);
        }
      }
    };
  }, [districtId]);

  // ── Field update + autosave ──────────────────────────────────────────────────
  const updateField = useCallback(
    (field, value) => {
      setNote((prev) => {
        if (!prev) return prev;
        return { ...prev, [field]: value, dirty: true };
      });

      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);

      autosaveTimer.current = setTimeout(async () => {
        const current = latestNote.current;
        if (!current || !districtId) return;

        setSaving(true);
        try {
          const updated = await notesService.updateNote(
            current.id,
            {
              title:           current.title,
              content:         current.content,
              tags:            current.tags,
              note_type:       current.note_type,
              checklist_items: current.checklist_items,
              category:        current.category,
              visibility_type: current.visibility_type,
              pinned:          current.pinned,
              archived:        current.archived,
            },
            districtId
          );
          setNote(updated);
          setSavedAt(Date.now());
          invalidateQuery(["notes", districtId]);
          invalidateQuery(["search-corpus", districtId]);
        } catch {
          // Autosave failure is silent — dirty flag keeps the data safe locally
        } finally {
          setSaving(false);
        }
      }, AUTOSAVE_DELAY);
    },
    [districtId]
  );

  // ── Explicit field toggle (pin / archive) — save immediately ─────────────────
  const toggleField = useCallback(
    async (field) => {
      if (!note || !districtId) return;
      const newVal = !note[field];
      const optimistic = { ...note, [field]: newVal };
      setNote(optimistic);

      try {
        const updated = await notesService.updateNote(
          note.id,
          { [field]: newVal },
          districtId
        );
        setNote(updated);
        invalidateQuery(["notes", districtId]);
        invalidateQuery(["search-corpus", districtId]);
      } catch {
        setNote(note); // revert
      }
    },
    [note, districtId]
  );

  // ── Delete ────────────────────────────────────────────────────────────────────
  const remove = useCallback(async () => {
    if (!note || !districtId) return;

    // Cancel pending autosave and null the ref so the unmount-flush guard
    // (which checks `if (autosaveTimer.current)`) does not attempt to save
    // the deleted note back to storage.
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = null;

    // Null the note ref before navigation so any concurrent async path
    // that checks latestNote.current skips the update.
    latestNote.current = null;

    await notesService.deleteNote(note.id, districtId);
    invalidateQuery(["notes", districtId]);
    invalidateQuery(["search-corpus", districtId]);

    // Drop the deleted note's path from the in-app history stack so the
    // back button can never return to a deleted entity's URL.
    NavHistory.dropPath(`/notes/${note.id}`);
    nav("/notes", { replace: true });
  }, [note, districtId, nav]);

  return {
    note,
    loading,
    saving,
    savedAt,
    updateField,
    toggleField,
    remove,
  };
}
